import { DeicingGridRowSummary } from "@/component/ItemList/useItemListLogicOdmraz";
import { jsPDF } from "jspdf";

export type DeicingProtocolPdfOptions = {
    fileName?: string;
    logoDataUrl?: string;
};

const fontCache = new Map<string, Promise<string>>();

function loadFontBase64(url: string): Promise<string> {
    if (!fontCache.has(url)) {
        fontCache.set(url, fetch(url)
            .then(r => {
                if (!r.ok) throw new Error(`Font ${url}: ${r.status}`);
                return r.arrayBuffer();
            })
            .then(buf => {
                const bytes = new Uint8Array(buf);
                let bin = "";
                for (let i = 0; i < bytes.length; i += 0x8000)
                    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
                return btoa(bin);
            }));
    }
    return fontCache.get(url)!;
}

const FONTS = {
    normal: "/fonts/Roboto-Regular.ttf",
    bold: "/fonts/Roboto-Bold.ttf",
};

export async function exportDeicingProtocolPdf(
    row: DeicingGridRowSummary,
    opts: DeicingProtocolPdfOptions = {}
) {
    const {
        fileName = `Deicing-${row.flightNumber ?? "protocol"}.pdf`,
        logoDataUrl
    } = opts;

    const doc = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait"
    });

    for (const [style, url] of Object.entries(FONTS)) {
        const file = url.split("/").pop()!;
        doc.addFileToVFS(file, await loadFontBase64(url));
        doc.addFont(file, "Roboto", style);
    }

    const pageW = doc.internal.pageSize.getWidth();

    const margin = 8;
    const x = margin;
    const width = pageW - margin * 2;

    let y = 8;

    doc.setLineWidth(0.35);
    doc.setDrawColor(0);
    doc.setTextColor(0);

    // ============================================================
    // HELPERS
    // ============================================================

    const text = (
        value: string | number | null | undefined,
        tx: number,
        ty: number,
        options?: {
            size?: number;
            bold?: boolean;
            align?: "left" | "center" | "right";
        }
    ) => {
        const {
            size = 6,
            bold = false,
            align = "left"
        } = options ?? {};

        doc.setFontSize(size);
        doc.setFont("Roboto", bold ? "bold" : "normal");

        doc.text(
            value == null ? "" : String(value),
            tx,
            ty,
            { align }
        );
    };

    const line = (
        x1: number,
        y1: number,
        x2: number,
        y2: number
    ) => {
        doc.line(x1, y1, x2, y2);
    };

    const rect = (
        rx: number,
        ry: number,
        rw: number,
        rh: number
    ) => {
        doc.rect(rx, ry, rw, rh);
    };

    const checkbox = (
        cx: number,
        cy: number,
        checked = false
    ) => {
        const size = 3;

        rect(cx, cy, size, size);

        if (checked) {
            text(
                "X",
                cx + size / 2,
                cy + 2.5,
                {
                    size: 6,
                    bold: true,
                    align: "center"
                }
            );
        }
    };

    const formatDate = (
        value?: string | number | null
    ) => {
        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return date.toLocaleDateString("cs-CZ");
    };

    const dateFormat = (date) => {
        const d = new Date(date);

        const pad = (n) => String(n).padStart(2, "0");

        return (
            `${pad(d.getDate())}.` +
            `${pad(d.getMonth() + 1)}.` +
            `${d.getFullYear()} ` +
            `${pad(d.getHours())}:` +
            `${pad(d.getMinutes())}`
        );
    };

    // const formatTime = (
    //     value?: number | string | null
    // ) => {
    //     if (!value) {
    //         return "";
    //     }

    //     const date = new Date(value);

    //     if (Number.isNaN(date.getTime())) {
    //         return "";
    //     }

    //     return date.toLocaleTimeString("cs-CZ", {
    //         hour: "2-digit",
    //         minute: "2-digit"
    //     });
    // };

    const formatTime = (value?: number | string | null) => {
        if (value == null) {
            return "";
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return "";
        }

        const pad = (n: number) => String(n).padStart(2, "0");

        return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    };

    const getStart = () => {
        const values = [
            row.type1Start,
            row.type2Start
        ].filter(
            (value): value is number =>
                value != null
        );

        return values.length
            ? Math.min(...values)
            : null;
    };

    const getEnd = () => {
        const values = [
            row.type1End,
            row.type2End
        ].filter(
            (value): value is number =>
                value != null
        );

        return values.length
            ? Math.max(...values)
            : null;
    };

    // ============================================================
    // HEADER
    // ============================================================

    const headerH = 22;

    rect(
        x,
        y,
        width,
        headerH
    );

    const logoW = 57;

    // line(
    //     x + logoW,
    //     y,
    //     x + logoW,
    //     y + headerH
    // );

    if (logoDataUrl) {
        doc.addImage(
            logoDataUrl,
            "PNG",
            x + 5,
            y + 5,
            45,
            15
        );
    }

    const headerCenterX =
        x + logoW + (width - logoW) / 2;

    text(
        "ZÁZNAM O PROVEDENÉM ODMRAZOVÁNÍ LETADLA",
        headerCenterX,
        y + 5,
        {
            size: 8.5,
            bold: true,
            align: "center"
        }
    );

    text(
        "PREVENCE PROTI NÁMRAZE",
        headerCenterX,
        y + 10,
        {
            size: 8,
            bold: true,
            align: "center"
        }
    );

    text(
        "RECORD ABOUT DE-ICING / ANTI-ICING OF A/C",
        headerCenterX,
        y + 16,
        {
            size: 8,
            bold: true,
            align: "center"
        }
    );

    y += headerH;

    // ============================================================
    // MAIN TABLE WIDTHS
    // ============================================================

    const leftW = 95;
    const rightX = x + leftW;
    const rightW = width - leftW;

    const leftLabelW = 58;
    const rightLabelW = 31;

    const drawLeftValueRow = (
        rowY: number,
        h: number,
        label: string,
        value?: string | number | null,
        options?: {
            secondLabel?: string;
        }
    ) => {
        rect(
            x,
            rowY,
            leftW,
            h
        );

        line(
            x + leftLabelW,
            rowY,
            x + leftLabelW,
            rowY + h
        );

        if (options?.secondLabel) {
            text(
                label,
                x + 2,
                rowY, //+ 4.5,
                {
                    size: 5.5,
                    bold: true
                }
            );

            text(
                options.secondLabel,
                x + 2,
                rowY, //+ 9,
                {
                    size: 5.2,
                    bold: true
                }
            );
        } else {
            text(
                label,
                x + 2,
                rowY + h / 2 + 1,
                {
                    size: 5.5,
                    bold: true
                }
            );
        }

        text(
            value ?? "",
            x + leftLabelW + 2,
            rowY + h / 2 + 1,
            {
                size: 7,
                bold: true
            }
        );
    };

    // ============================================================
    // DATE, FLIGHT, AIRCRAFTTYPE + SPRAYERS
    // ============================================================

    const dateH = 6;

    drawLeftValueRow(
        y,
        dateH,
        "DATUM | DATE",
        formatDate(row.date)
    );

    rect(
        rightX,
        y,
        rightW,
        dateH
    );

    drawLeftValueRow(
        y + dateH,
        dateH,
        "LET | FLIGHT",
        row.flightNumber
    );

    drawLeftValueRow(
        y + 2 * dateH,
        dateH,
        "TYP LETADLA | A/C TYPE",
        row.aircraftType
    );

    drawLeftValueRow(
        y + 3 * dateH,
        dateH,
        "IMATRIKULAČNÍ ZNAČKA | REGISTRATION",
        row.aircraftRegistration
    );

    // ============================================================
    // PROCEDURE START / END
    // ============================================================

    const procedureH = 6;
    const procedureY = y + 4 * dateH;

    rect(
        x,
        procedureY,
        leftW,
        procedureH
    );

    rect(
        x,
        procedureY + procedureH,
        leftW,
        procedureH
    );

    // rect(
    //     rightX,
    //     y,
    //     rightW,
    //     procedureH
    // );

    text(
        "OPERACE ZAPOČATA V | PROCEDURE WAS STARTED AT",
        x + 2,
        procedureY + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    // text(
    //     "PROCEDURE WAS STARTED AT",
    //     x + 2,
    //     y + 8,
    //     {
    //         size: 5.2
    //     }
    // );

    text(
        formatTime(getStart()),
        x + leftW - 3,
        procedureY + 4,
        {
            size: 7.5,
            bold: true,
            align: "right"
        }
    );

    text(
        "OPERACE SKONČENA V | PROCEDURE WAS FINISHED AT",
        x + 2,
        procedureY + procedureH + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    // text(
    //     "PROCEDURE WAS FINISHED AT",
    //     rightX + 2,
    //     y + 8,
    //     {
    //         size: 5.2
    //     }
    // );

    text(
        formatTime(getEnd()),
        x + leftW - 3,
        procedureY + procedureH + 4,
        {
            size: 7.5,
            bold: true,
            align: "right"
        }
    );

    // rect(
    //     rightX,
    //     y,
    //     rightW,
    //     registrationH
    // );

    const sprayerH = 6; //dateH / 3;

    [
        ["SPRAYER # 1", row?.nextOperator1 && row?.operator1 ? `${row.operator1}, ${row.nextOperator1}` : row?.operator1 ?? row.nextOperator1],
        ["SPRAYER # 2", row?.nextOperator2 && row?.operator2 ? `${row.operator2}, ${row.nextOperator2}` : row?.operator2 ?? row.nextOperator2],
        ["SPRAYER # 3", row?.nextOperator3 && row?.operator3 ? `${row.operator3}, ${row.nextOperator3}` : row?.operator3 ?? row.nextOperator3]
    ].forEach(([label, value], index) => {
        const sy =
            y + index * sprayerH;

        if (index > 0) {
            line(
                rightX,
                sy,
                rightX + rightW,
                sy
            );
        }

        line(
            rightX + rightLabelW,
            sy,
            rightX + rightLabelW,
            sy + sprayerH
        );

        line(
            rightX + rightW,
            sy,
            rightX + rightW,
            sy + sprayerH
        );

        text(
            label,
            rightX + 2,
            sy + 4,
            {
                size: 5.5,
                bold: true
            }
        );

        text(
            value,
            rightX + rightLabelW + 2,
            sy + 4,
            {
                size: 6.5,
                bold: true
            }
        );
    });

    y += 3 * dateH;//dateH;

    // ============================================================
    // FLIGHT + STAND / BRIDGE
    // ============================================================

    const smallRowH = 6;
    const flightH = smallRowH * 2;

    // drawLeftValueRow(
    //     y,
    //     flightH,
    //     "LET | FLIGHT",
    //     row.flightNumber
    // );

    // DE-ICING STAND
    rect(
        rightX,
        y,
        rightW,
        smallRowH
    );

    line(
        rightX + rightLabelW,
        y,
        rightX + rightLabelW,
        y + smallRowH
    );

    text(
        "DE-ICING STAND",
        rightX + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        row.stand,
        rightX + rightLabelW + 2,
        y + 4,
        {
            size: 7,
            bold: true
        }
    );

    // BRIDGE
    const bridgeY =
        y + smallRowH;

    rect(
        rightX,
        bridgeY,
        rightW,
        smallRowH
    );

    text(
        "BRIDGE",
        rightX + 2,
        bridgeY + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "ANO | YES",
        rightX + 29,
        bridgeY + 4,
        {
            size: 5.3
        }
    );

    checkbox(
        rightX + 55,
        bridgeY + 2,
        row?.most
    );

    text(
        "NE | NO",
        rightX + 62,
        bridgeY + 4,
        {
            size: 5.3
        }
    );

    checkbox(
        rightX + 78,
        bridgeY + 2,
        !row?.most
    );

    y += flightH;

    // ============================================================
    // AIRCRAFT TYPE + TRUCK / HOLDOVER
    // ============================================================

    const aircraftTypeH =
        smallRowH * 2;

    // drawLeftValueRow(
    //     y,
    //     aircraftTypeH,
    //     "TYP LETADLA | A/C TYPE",
    //     row.aircraftType
    // );

    // DE-ICING TRUCK
    rect(
        rightX,
        y,
        rightW,
        smallRowH
    );

    line(
        rightX + rightLabelW,
        y,
        rightX + rightLabelW,
        y + smallRowH
    );

    text(
        "DE-ICING TRUCK",
        rightX + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        [
            row.deice1,
            row.deice2,
            row.deice3
        ]
            .filter(Boolean)
            .join(", "),
        rightX + rightLabelW + 2,
        y + 4,
        {
            size: 5.5,
            bold: false
        }
    );

    y += smallRowH;

    // ============================================================
    // FLUID / WATER HEADER
    // ============================================================

    const fluidHeaderH = 6;

    rect(
        x,
        y,
        rightW + leftW,
        fluidHeaderH
    );

    // rect(
    //     rightX,
    //     y,
    //     rightW,
    //     fluidHeaderH
    // );

    text(
        "POUŽITÁ KAPALINA | FLUID USED",
        x + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    // text(
    //     "MNOŽSTVÍ VODY | WATER AMOUNT",
    //     rightX + 2,
    //     y + 4,
    //     {
    //         size: 5.5,
    //         bold: true
    //     }
    // );

    // const waterUnitW = 9;

    // line(
    //     rightX + rightW - waterUnitW,
    //     y,
    //     rightX + rightW - waterUnitW,
    //     y + fluidHeaderH
    // );

    // text(
    //     row.waterConsumption ?? "",
    //     rightX + rightW - waterUnitW - 2,
    //     y + 4.2,
    //     {
    //         size: 6,
    //         bold: true,
    //         align: "right"
    //     }
    // );

    // text(
    //     "ltr.",
    //     rightX + rightW - waterUnitW / 2,
    //     y + 4.2,
    //     {
    //         size: 5,
    //         align: "center"
    //     }
    // );

    y += fluidHeaderH;

    // ============================================================
    // TYPE I + TYPE II
    // ============================================================

    const fluidH = 20;
    const fluidColumnW = width / 3;

    rect(
        x,
        y,
        fluidColumnW,
        fluidH
    );

    rect(
        x + fluidColumnW,
        y,
        fluidColumnW,
        fluidH
    );

    rect(
        x + 2 * fluidColumnW,
        y,
        fluidColumnW,
        fluidH
    );

    const amountW = 25;
    const amountUnitW = 8;
    const ratioW = 24;
    const ratioUnitW = 8;

    const inputY = y + 7;
    const inputH = 7;

    // TYPE I
    const type1X = x;

    text(
        "TYP I / TYPE I",
        type1X + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "Koncentrace",
        type1X + fluidColumnW - ratioW + 3,
        y + 4,
        {
            size: 5,
            align: "center"
        }
    );

    rect(
        type1X + 2,
        inputY,
        amountW,
        inputH
    );

    line(
        type1X + 2 + amountW - amountUnitW,
        inputY,
        type1X + 2 + amountW - amountUnitW,
        inputY + inputH
    );

    text(
        row.type1FluidConsumption ?? "",
        type1X + 2 + amountW - amountUnitW - 2,
        inputY + 5,
        {
            size: 6,
            bold: true,
            align: "right"
        }
    );

    text(
        "ltr.",
        type1X + 2 + amountW - amountUnitW / 2,
        inputY + 5,
        {
            size: 5,
            align: "center"
        }
    );

    rect(
        type1X + fluidColumnW - ratioW - 2,
        inputY,
        ratioW,
        inputH
    );

    line(
        type1X + fluidColumnW - ratioUnitW - 2,
        inputY,
        type1X + fluidColumnW - ratioUnitW - 2,
        inputY + inputH
    );

    text(
        row.fluid1Ratio ?? "",
        type1X + fluidColumnW - ratioUnitW - 4,
        inputY + 5,
        {
            size: 6,
            bold: true,
            align: "right"
        }
    );

    text(
        "%",
        type1X + fluidColumnW - ratioUnitW / 2 - 2,
        inputY + 5,
        {
            size: 5,
            align: "center"
        }
    );

    // TYPE II
    const type2X =
        x + fluidColumnW;

    text(
        "TYP II / TYPE II",
        type2X + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "Koncentrace",
        type2X + fluidColumnW - 21,
        y + 4,
        {
            size: 5,
            align: "center"
        }
    );

    rect(
        type2X + 2,
        inputY,
        amountW,
        inputH
    );

    line(
        type2X + 2 + amountW - amountUnitW,
        inputY,
        type2X + 2 + amountW - amountUnitW,
        inputY + inputH
    );

    text(
        row.type2FluidConsumption ?? "",
        type2X + 2 + amountW - amountUnitW - 2,
        inputY + 5,
        {
            size: 6,
            bold: true,
            align: "right"
        }
    );

    text(
        "ltr.",
        type2X + 2 + amountW - amountUnitW / 2,
        inputY + 5,
        {
            size: 5,
            align: "center"
        }
    );

    rect(
        type2X + fluidColumnW - ratioW - 2,
        inputY,
        ratioW,
        inputH
    );

    line(
        type2X + fluidColumnW - ratioUnitW - 2,
        inputY,
        type2X + fluidColumnW - ratioUnitW - 2,
        inputY + inputH
    );

    text(
        row.fluid2Ratio ?? "",
        type2X + fluidColumnW - ratioUnitW - 4,
        inputY + 5,
        {
            size: 6,
            bold: true,
            align: "right"
        }
    );

    text(
        "%",
        type2X + fluidColumnW - ratioUnitW / 2 - 2,
        inputY + 5,
        {
            size: 5,
            align: "center"
        }
    );

    // WATER
    const type3X =
        x + 2 * fluidColumnW;

    text(
        "MNOŽSTVÍ VODY | WATER AMOUNT",
        type3X + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    rect(
        type3X + 2,
        inputY,
        amountW,
        inputH
    );

    line(
        type3X + 2 + amountW - amountUnitW,
        inputY,
        type3X + 2 + amountW - amountUnitW,
        inputY + inputH
    );

    text(
        row.waterConsumption ?? "",
        type3X + 2 + amountW - amountUnitW - 2,
        inputY + 5,
        {
            size: 6,
            bold: true,
            align: "right"
        }
    );

    text(
        "ltr.",
        type3X + 2 + amountW - amountUnitW / 2,
        inputY + 5,
        {
            size: 5,
            align: "center"
        }
    );

    y += fluidH;

    // ============================================================
    // CLEAR ICE CHECK
    // ============================================================

    const clearCheckH = 10;

    rect(
        x,
        y,
        width / 2,
        clearCheckH
    );

    rect(
        x + width / 2,
        y,
        width / 2,
        clearCheckH
    );

    const halfW = width / 2;

    line(
        x + halfW,
        y,
        x + halfW,
        y + clearCheckH
    );

    text(
        "KONTROLA ČISTÉHO LEDU PROVEDENA, LED NENALEZEN",
        x + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "CLEAR CHECK PERFORMED, NO CLEAR ICE FOUND",
        x + 2,
        y + 7,
        {
            size: 5
        }
    );

    checkbox(
        x + halfW - 6,
        y + 3.5,
        !!row.checkedBy
    );

    text(
        "LED NALEZEN",
        x + halfW + 2,
        y + 4,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "CLEAR ICE FOUND",
        x + halfW + 2,
        y + 7,
        {
            size: 5
        }
    );

    checkbox(
        x + width - 6,
        y + 3.5
    );

    y += clearCheckH;

    // ============================================================
    // WEATHER
    // ============================================================

    const weatherH = 12;

    rect(
        x,
        y,
        width,
        weatherH
    );

    line(
        x + width / 2,
        y,
        x + width / 2,
        y + weatherH
    );

    line(
        x,
        y + weatherH / 2,
        x + width / 2,
        y + weatherH / 2
    );

    text(
        "OAT (°C) : " + (row.oat ?? ""),
        x + 2,//+ 8,
        y + 4,
        {
            size: 5.5,
            bold: true,
        }
    );

    text(
        "WING SKIN TEMP (°C) : " + (row.wingTemperature ?? ""),
        x + 2, //+ 14,
        y + weatherH / 2 + 4,
        {
            size: 5.5,
            bold: true,
        }
    );

    text(
        row.weatherName ?? "",
        x + width / 2 + 2,
        y + 5,
        {
            size: 6,
            bold: true
        }
    );

    text(
        row.weatherEngName ?? "",
        x + width / 2 + 2,
        y + 8,
        {
            size: 5.5
        }
    );

    y += weatherH;

    // ============================================================
    // DAMAGE / POST CHECK
    // ============================================================

    const damageH = 14;

    rect(
        x,
        y,
        width / 2,
        damageH
    );

    rect(
        x + width / 2,
        y,
        width / 2,
        damageH
    );

    text(
        "LETADLO NEBYLO PŘI ODMRAZOVÁNÍ VIDITELNĚ POŠKOZENO",
        x + 2,
        y + 5,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "A/C HAS BEEN CHECKED AND THERE IS NO EVIDENCE DAMAGE OCCURRED DURING TOWING AND",
        x + 2,
        y + 8,
        {
            size: 5
        }
    );

    text(
        "DE-ICING / ANTI-ICING PROCESS",
        x + 2,
        y + 11,
        {
            size: 5
        }
    );

    text(
        "KONTROLA PO ODMRAZOVÁNÍ (OCHRANA PROTI NÁMRAZE) BYLA PROVEDENA",
        x + width / 2 + 2,
        y + 5,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "POST DE-ICING (ANTI-ICING) CHECK COMPLETED",
        x + width / 2 + 2,
        y + 8,
        {
            size: 5
        }
    );

    y += damageH;

    // ============================================================
    // SUPERVISOR
    // ============================================================

    const supervisorH = 14;
    const supervisorLabelW = width / 2;

    rect(
        x,
        y,
        width,
        supervisorH
    );

    line(
        x + supervisorLabelW,
        y,
        x + supervisorLabelW,
        y + supervisorH
    );

    text(
        "JMÉNO A PODPIS SUPERVISORA ODMRAZOVÁNÍ",
        x + 2,
        y + 6,
        {
            size: 5.5,
            bold: true
        }
    );

    text(
        "NAME AND SIGNATURE OF DE-ICING SUPERVISOR",
        x + 2,
        y + 9,
        {
            size: 5
        }
    );

    text(
        row.checkedBy ?? "",
        x + supervisorLabelW + 4,
        y + 6,
        {
            size: 8,
            bold: true
        }
    );

    text(
        row?.checkedDate ? dateFormat(row?.checkedDate) : "",
        x + supervisorLabelW + 4,
        y + 10,
        {
            size: 6
        }
    );

    y += supervisorH;

    rect(
        x,
        y,
        width,
        10
    );

    text(
        "CSAH-F-412, verze č. 6",
        x + 2,
        y + 4,
        {
            size: 6
        }
    );

    text(
        "(zavedeno dokumentem: CSAH-MN-10)",
        x + 2,
        y + 7,
        {
            size: 6
        }
    );





    doc.save(fileName);
}